<template>
  <div class="container-fluid">

    <div v-if="loading" class="z-depth-1 white center-align" style="height: 250px">
      <div class="row" style="padding-top: 100px">
        <div class="preloader-wrapper active">
          <div class="spinner-layer spinner-white-only">
            <div class="circle-clipper left">
              <div class="circle"></div>
            </div><div class="gap-patch">
              <div class="circle"></div>
            </div><div class="circle-clipper right">
              <div class="circle"></div>
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <span>Loading data...</span>
      </div>
    </div>

    <div v-else class="row">
      <div class="col s10">
        <div class="row occupied-spacing-right">
          <div class="col s12 z-depth-1 white">
            <div class="section">
              <h5>{{$t('occupiedevents')}}</h5>
            </div>

            <highcharts :options="chartOptions" />
          </div>
        </div>
      </div>
      <div class="col s2">
        <div class="col s12 z-depth-1 white" style="height: 385px;">
          <div class="section">
            <h5>Filter</h5>
          </div>

          <div class="input-field" style="">
            <period-selector v-model="selectedPeriod" :periods="periods"/>
            <br/>
            <br/>
            <date-picker @input="dateChanged" v-model="date" :max-date="maxDate" @dateChanged="datePickerChanged" style="padding-right: 6px;" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue'
import moment, { now } from 'moment'
import PeriodSelector from '@/components/PeriodSelector'
import DatePicker from '@/components/switch/DatePicker'
import SectionOccupationsService from '@/services/api/axleshunt/SectionOccupationsService'
import SectionAlarmsService from '@/services/api/axleshunt/SectionAlarmService'
import Highcharts from 'highcharts'

export default {
  name: 'DigitalEvents',
  props: {
    assets: { type: Object, required: true },
    railObjectId: { type: [Number, null] }
  },
  components: {
    HighChart: Chart,
    'datePicker': DatePicker,
    PeriodSelector
  },
  data () {
    return {
      measurementData: undefined,
      shuntStates: undefined,
      loading: false,
      date: {
        start: moment().endOf('day').subtract(1, 'w').format(),
        end: moment().endOf('day').format()
      },
      selectedPeriod: '1week',
      maxDate: new Date(),
      periods: {
        '1day': 'Laatste dag',
        '3days': 'Laatste 3 dagen',
        '1week': 'Laatste week',
        '2weeks': this.$t('periodselection.last_two_weeks'),
        '1month': this.$t('periodselection.last_month')
      },
      chartOptions: {
        chart: {
          zoomType: 'x',
          height: 300,
          zooming: {
            mouseWheel: {
              enabled: true
            }
          },
          events: {
            load () {
              const chart = this

              // Block scroll wheel unless Shift is held
              chart._wheelBlocker = (e) => {
                if (!e.shiftKey) {
                  if (e.stopImmediatePropagation) e.stopImmediatePropagation()
                  e.stopPropagation()
                }
              }
              chart.container.addEventListener('wheel', chart._wheelBlocker, { capture: true })

              // Show zoom cursor when Shift is held
              chart._cursorHint = (e) => {
                chart.container.style.cursor = e.shiftKey ? 'zoom-in' : ''
              }
              chart.container.addEventListener('mousemove', chart._cursorHint)

              // Flip cursor immediately on Shift press/release
              chart._onKeyShift = (e) => {
                if (e.key === 'Shift' && chart.container) {
                  chart.container.style.cursor = (e.type === 'keydown') ? 'zoom-in' : ''
                }
              }
              chart._offKeyDown = Highcharts.addEvent(document, 'keydown', chart._onKeyShift)
              chart._offKeyUp = Highcharts.addEvent(document, 'keyup', chart._onKeyShift)
            },
            destroy () {
              const el = this.container
              if (this._wheelBlocker && el) el.removeEventListener('wheel', this._wheelBlocker, { capture: true })
              if (this._cursorHint && el) el.removeEventListener('mousemove', this._cursorHint)
              if (this._offKeyDown) this._offKeyDown()
              if (this._offKeyUp) this._offKeyUp()
            }
          }
        },
        plotOptions: {
          series: {
            allowPointSelect: true,
            animation: false
          }
        },
        accessibility: {
          enabled: false
        },
        exporting: {
          enabled: false
        },
        title: {
          text: ''
        },
        credits: {
          enabled: false
        },
        time: {
          useUTC: false
        },
        subtitle: {
          text: ''
        },
        legend: {
          enabled: true
        },
        tooltip: {
          enabled: true,
          formatter: function () {
            if (this.point.timestamp !== undefined) {
              return 'Time: <b>' + moment(this.point.timestamp.valueOf()).format('DD-MM-yyyy HH:mm:ss') + '</b><br>Occupation time: <b>' + this.point.occupation_time.toFixed(1) + ' s</b><br>' + this.point.message
            }
            return 'Time: <b></b><br>Occupation time: <b>' + this.point.occupation_time.toFixed(1) + ' s</b><br>' + this.point.message
          }
        },
        yAxis: {
          type: 'linear',
          min: 0,
          max: 2,
          tickInterval: 1,
          title: {
            enabled: false
          }
        },
        xAxis: {
          type: 'datetime'
        },
        series: [{
          name: 'Section',
          type: 'line',
          data: [],
          step: true,
          enableMouseTracking: false,
          turboThreshold: 10000,
          marker: {
            symbol: 'circle',
            radius: 1
          },
          states: {
            inactive: {
              opacity: 1
            }
          }
        }, {
          name: 'Section',
          type: 'scatter',
          data: [],
          enableMouseTracking: true,
          turboThreshold: 10000,
          linkedTo: ':previous',
          marker: {
            symbol: 'circle',
            radius: 3
          }
        }, {
          name: 'Section',
          type: 'scatter',
          data: [],
          enableMouseTracking: true,
          turboThreshold: 10000,
          linkedTo: ':previous',
          marker: {
            symbol: 'circle',
            radius: 8,
            fillColor: 'orange'
          }
        }, {
          name: 'Section',
          type: 'scatter',
          data: [],
          enableMouseTracking: true,
          turboThreshold: 10000,
          linkedTo: ':previous',
          marker: {
            symbol: 'triangle',
            radius: 8,
            fillColor: 'red'
          },
          zIndex: 2,
          yAxis: 0,
          stickyTracking: false
        }]
      }
    }
  },
  computed: {
    token () {
      return this.assets.token || ''
    }
  },
  watch: {
    '$route.params.id': async function (newVal, oldVal) {
      if (newVal !== oldVal) {
      }
    },
    selectedPeriod (period) {
      if (period === 'user_input') {
        return
      }
      const end = moment().startOf('day')
      const start = this.getStartDate(end)
      this.date = {
        start,
        end
      }
    }
  },
  mounted: async function () {
    await this.getOccupiedEvents()
  },
  methods: {
    async getOccupiedEvents () {
      this.loading = true

      this.chartOptions.series[0].data = []
      this.chartOptions.series[1].data = []
      this.chartOptions.series[2].data = []
      this.chartOptions.series[3].data = []
      var start = moment(this.date.start).startOf('day').utc().format()
      var end = moment(this.date.end).endOf('day').utc().format()
      this.measurementData = await this.getData(this.assets.uniqueIdR8, start, end)
      this.shuntStates = await this.getShuntStates(this.assets.uniqueIdR8, start, end)

      var LastTimestamp = now
      if (this.shuntStates !== null) {
        this.shuntStates.forEach(d => {
          if (LastTimestamp !== d.measuredDateTime) {
            if (d.severity !== null) {
              switch (d.severity.toLowerCase()) {
                case 'alarm':
                  this.chartOptions.series[3].data.push({ x: d.measuredDateTime + (d.shuntLength * 1000) / 2, y: 1.5, occupation_time: d.shuntLength, timestamp: d.measuredDateTime, message: d.message })
                  break
                case 'warning':
                  this.chartOptions.series[2].data.push({ x: d.measuredDateTime + (d.shuntLength * 1000) / 2, y: 1.5, occupation_time: d.shuntLength, timestamp: d.measuredDateTime, message: d.message })
                  break
                default:
                  break
              }
              LastTimestamp = d.measuredDateTime
            }
          }
        })
      }
      LastTimestamp = now
      this.measurementData.forEach(d => {
        if (LastTimestamp !== d.measuredDateTime) {
          this.chartOptions.series[1].data.push({ x: d.timestamp + (d.occupationTime * 1000) / 2, y: 1.3, occupation_time: d.occupationTime, measuredDateTime: d.timestamp, message: 'OK' })
          LastTimestamp = d.timestamp
        }
        d.stateChanges.forEach(sc => {
          this.chartOptions.series[0].data.push({ x: sc.timestamp, y: sc.value ? 1 : 0 })
        })
      })

      // Set X axis to display data for entire days using start and end of day
      this.chartOptions.xAxis.min = moment(start).valueOf()
      this.chartOptions.xAxis.max = moment(end).valueOf()

      this.loading = false
    },
    async getData (objectId, dtFrom, dtTo) {
      return SectionOccupationsService.getOccupiedDatas(objectId, this.token, dtFrom, dtTo)
    },
    async getShuntStates (objectId, dtFrom, dtTo) {
      return SectionAlarmsService.getShuntStates(this.token, objectId, dtFrom, dtTo)
    },
    dateChanged: async function () {
      await this.getOccupiedEvents()
    },
    datePickerChanged () {
      this.selectedPeriod = 'user_input'
    },
    getStartDate (endDate) {
      endDate = moment(endDate).endOf('day')
      switch (this.selectedPeriod) {
        case '1day':
          return moment(endDate).startOf('day')
        case '3days':
          return moment(endDate).subtract(3, 'day')
        case '1week':
          return moment(endDate).subtract(1, 'week')
        case '2weeks':
          return moment(endDate).subtract(2, 'week')
        case '1month':
          return moment(endDate).subtract(1, 'months')
        case '3months':
          return moment(endDate).subtract(3, 'months')
        case '6months':
          return moment(endDate).subtract(6, 'months')
        case '1year':
          return moment(endDate).subtract(1, 'years')
        default:
          return moment(endDate).subtract(1, 'months')
      }
    }
  }
}
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/pages/axleshunt/Digital/Index.vue